import React, { useState, useEffect, useRef } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { Stars, OrbitControls } from "@react-three/drei";
import { motion } from "framer-motion";
import {
  ChevronRight,
  ChevronDown,
  Car,
  Building,
  Compass,
  Construction,
  Menu,
  X,
  Mail,
  Phone,
  MapPin,
  Star,
  Award,
  ArrowRight,
  Clock,
  Calendar,
  User,
  Send,
  Sun,
  Home,
  Film,
  Volume2,
  Coffee,
} from "lucide-react";
import AOS from "aos";
import "aos/dist/aos.css";
import { useNavigate } from "react-router-dom";

// Custom floating particles
const FloatingParticles = () => {
  const group = useRef();
  const [particleColor, setParticleColor] = useState("#FFD700"); // Default for light theme

  useEffect(() => {
    const updateColorBasedOnTheme = () => {
      const isDarkMode = document.documentElement.classList.contains("dark");
      setParticleColor(isDarkMode ? "#FFB703" : "#CC7722");
    };

    updateColorBasedOnTheme();

    // Listen for class changes (optional, for dynamic toggle)
    const observer = new MutationObserver(updateColorBasedOnTheme);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => observer.disconnect();
  }, []);

  useFrame(({ clock }) => {
    if (group.current) {
      group.current.rotation.y = clock.getElapsedTime() * 0.1;
    }
  });

  return (
    <group ref={group}>
      {[...Array(200)].map((_, i) => (
        <mesh
          key={i}
          position={[
            (Math.random() - 0.5) * 20,
            (Math.random() - 0.5) * 20,
            (Math.random() - 0.5) * 20,
          ]}
        >
          <sphereGeometry args={[0.05, 16, 16]} />
          <meshBasicMaterial color={particleColor} />
        </mesh>
      ))}
    </group>
  );
};

const LuxuryBackground = () => (
  <Canvas camera={{ position: [0, 0, 10] }}>
    <ambientLight intensity={0.3} />
    <pointLight position={[10, 10, 10]} color="#FFD700" intensity={1} />
    <Stars radius={30} depth={60} count={5000} factor={4} saturation={0} fade />
    <FloatingParticles />
    <OrbitControls enableZoom={false} autoRotate autoRotateSpeed={0.5} />
  </Canvas>
);

const DevBhoomiBusinessPage = () => {
  const [activeSection, setActiveSection] = useState("hero");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showAutomobileDropdown, setShowAutomobileDropdown] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const navigate = useNavigate();

  // Handle scroll effects
  useEffect(() => {
    const handleScroll = () => {
      // Check if page is scrolled for navbar style change
      if (window.scrollY > 100) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }

      // Detect active section for navigation
      const sections = [
        "hero",
        "steel",
        "automobile",
        "tourism",
        "infrastructure",
      ];

      sections.forEach((section) => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (
            rect.top <= window.innerHeight / 3 &&
            rect.bottom >= window.innerHeight / 3
          ) {
            setActiveSection(section);
          }
        }
      });
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      window.scrollTo({
        top: element.offsetTop - 80,
        behavior: "smooth",
      });
      setActiveSection(sectionId);
      setMobileMenuOpen(false);
    }
  };

  // Animation variants
  const fadeInVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
    },
  };

  const fadeInRightVariants = {
    hidden: { opacity: 0, x: -50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
    },
  };

  const fadeInLeftVariants = {
    hidden: { opacity: 0, x: 50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
    },
  };

  const staggerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const scaleUpVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
    },
  };

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="font-sans bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100 overflow-x-hidden">
      {/* Hero Section */}
      <section className="relative min-h-screen overflow-hidden bg-gradient-to-b from-amber-50 to-gray-100 dark:from-black dark:to-slate-900">
        {/* 3D Background */}
        <div className="absolute inset-0 z-0">
          <LuxuryBackground />
        </div>

        {/* Light theme decoration elements */}
        <div className="absolute top-20 left-10 w-64 h-64 bg-amber-200 rounded-full filter blur-3xl opacity-20 animate-pulse hidden md:block"></div>
        <div className="absolute bottom-20 right-10 w-80 h-80 bg-amber-300 rounded-full filter blur-3xl opacity-20 animate-pulse hidden md:block"></div>
        <div className="absolute top-1/2 left-1/4 w-32 h-32 bg-amber-400 rounded-full filter blur-3xl opacity-10 animate-pulse hidden md:block"></div>

        {/* Overlay content */}
        <div className="relative z-10 flex flex-col items-center justify-center min-h-screen text-center px-6">
          <div className="inline-flex items-center space-x-2 rounded-full border border-amber-600/30 bg-white/90 dark:bg-black/80 px-5 py-2.5 backdrop-blur-sm shadow-lg transform hover:scale-105 transition-all duration-300">
            <div className="h-2.5 w-2.5 rounded-full bg-amber-500 animate-pulse"></div>
            <span className="font-serif text-sm text-amber-700 dark:text-amber-300 font-medium">
              EXCELLENCE SINCE 2005
            </span>
          </div>

          <h1 className="mt-10 font-serif text-5xl xl:text-6xl 2xl:text-7xl tracking-tight text-gray-900 dark:text-white font-bold drop-shadow-sm">
            <span className="block">Building</span>
            <span className="mt-2 block bg-gradient-to-r from-amber-300 via-amber-500 to-amber-700 bg-clip-text text-transparent">
              Excellence
            </span>
            Across Industries
          </h1>

          <p className="mt-8 text-lg md:text-xl font-light leading-relaxed text-gray-700 dark:text-gray-300 max-w-3xl backdrop-blur-sm bg-white/30 dark:bg-transparent py-4 px-6 rounded-xl">
            From Steel to Hospitality, the Dev Bhoomi Group Companies is
            committed to quality, innovation,
            <span className="hidden md:inline">
              {" "}
              and excellence in everything we do.
            </span>
          </p>

          <div className="mt-12 flex flex-wrap justify-center gap-6">
            <button
              onClick={() => scrollToSection("automobile")}
              className="group relative overflow-hidden rounded-lg border-2 border-amber-500 bg-amber-50 hover:bg-amber-500 px-8 py-2 text-lg font-medium text-amber-700 hover:text-white dark:bg-transparent dark:text-amber-300 dark:hover:text-white transition duration-300 shadow-lg hover:shadow-xl"
            >
              Explore Our Ventures
            </button>
            <button
              onClick={() => navigate("/contact")}
              className="flex items-center text-base font-medium text-gray-800 dark:text-white transition duration-300 hover:text-amber-600 dark:hover:text-amber-300 bg-white/70 dark:bg-transparent hover:bg-white dark:hover:bg-gray-600 px-6 py-2 rounded-lg shadow-md hover:shadow-lg"
            >
              Contact Us
            </button>
          </div>
        </div>
      </section>

      {/* Business Verticals Overview */}
      <section className="py-20 bg-white dark:bg-gray-900">
        <div className="container mx-auto px-6">
          <motion.div
            className="text-center mb-16"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeInVariants}
          >
            <span className="inline-block px-4 py-1 bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-100 rounded-full text-sm font-medium mb-4">
              OUR BUSINESS
            </span>
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900 dark:text-white">
              Business Verticals
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              Explore our diverse range of businesses across multiple
              industries, each committed to excellence and innovation.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Steel */}
            <motion.div
              className="group relative overflow-hidden rounded-2xl h-96 cursor-pointer"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={scaleUpVariants}
              onClick={() => scrollToSection("steel")}
            >
              <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/50 to-black/80 z-10"></div>
              <img
                src="https://static.wixstatic.com/media/11062b_bb02f705f6b6495bae35d7f1580451aa~mv2.jpeg/v1/crop/x_504,y_0,w_2000,h_2000/fill/w_355,h_355,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/Stacked%20Steel%20Rods.jpeg"
                alt="Steel Division"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute bottom-0 left-0 right-0 p-6 z-20 transform transition-transform duration-500 group-hover:translate-y-2">
                <div className="bg-amber-500 dark:bg-amber-500 w-10 h-10 rounded-full flex items-center justify-center mb-4">
                  <Building size={20} className="text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">Steel</h3>
                <p className="text-gray-200 mb-4">
                  High quality TMT bars established since 2005.
                </p>
                <span className="text-amber-400 dark:text-amber-400 flex items-center text-sm font-medium">
                  Learn More <ChevronRight size={16} className="ml-1" />
                </span>
              </div>
            </motion.div>

            {/* Automobile */}
            <motion.div
              className="group relative overflow-hidden rounded-2xl h-96 cursor-pointer"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={scaleUpVariants}
              onClick={() => scrollToSection("automobile")}
            >
              <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/50 to-black/80 z-10"></div>
              <img
                src="https://singleinterface-timeline-images.s3.ap-south-1.amazonaws.com/images/67021/fb_post_67021_2025_04_23_00_01_14_230147504432_1112898804205544.jpg"
                alt="Automobile Division"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute bottom-0 left-0 right-0 p-6 z-20 transform transition-transform duration-500 group-hover:translate-y-2">
                <div className="bg-amber-500 dark:bg-amber-500 w-10 h-10 rounded-full flex items-center justify-center mb-4">
                  <Car size={20} className="text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">
                  Automobile
                </h3>
                <p className="text-gray-200 mb-4">
                  Authorized dealerships of Skoda, Hyundai, and Tata Motors.
                </p>
                <span className="text-amber-400 dark:text-amber-400 flex items-center text-sm font-medium">
                  Learn More <ChevronRight size={16} className="ml-1" />
                </span>
              </div>
            </motion.div>

            {/* Tourism */}
            {/* Hospitality Division */}
            <motion.div
              className="group relative overflow-hidden rounded-2xl h-96 cursor-pointer"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={scaleUpVariants}
              onClick={() => scrollToSection("tourism")}
            >
              <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/50 to-black/80 z-10"></div>
              <img
                src="https://ik.imagekit.io/jncw2kb8u/hotel-in-una1.jpg?updatedAt=1745481996268"
                alt="Hospitality Division"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute bottom-0 left-0 right-0 p-6 z-20 transform transition-transform duration-500 group-hover:translate-y-2">
                <div className="bg-amber-500 dark:bg-amber-500 w-10 h-10 rounded-full flex items-center justify-center mb-4">
                  <Compass size={20} className="text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">
                  Hospitality
                </h3>
                <p className="text-gray-200 mb-4">
                  Experience luxury at The Gulmohar Grand with premium rooms and
                  the vibrant Food Street featuring KFC, Barista, and more.
                </p>
                <span className="text-amber-400 dark:text-amber-400 flex items-center text-sm font-medium">
                  Learn More <ChevronRight size={16} className="ml-1" />
                </span>
              </div>
            </motion.div>

            {/* Entertainment Division */}
            {/* <motion.div
              className="group relative overflow-hidden rounded-2xl h-96 cursor-pointer"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={scaleUpVariants}
              onClick={() => scrollToSection("entertainment")}
            >
              <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/50 to-black/80 z-10"></div>
              <img
                src="https://img.freepik.com/free-photo/rows-red-seats-theater_53876-64710.jpg"
                alt="Entertainment Division"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute bottom-0 left-0 right-0 p-6 z-20 transform transition-transform duration-500 group-hover:translate-y-2">
                <div className="bg-amber-500 dark:bg-amber-500 w-10 h-10 rounded-full flex items-center justify-center mb-4">
                  <Compass size={20} className="text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">
                  Entertainment
                </h3>
                <p className="text-gray-200 mb-4">
                  Watch the latest blockbusters in comfort and style at Grand
                  Cinemaz – the ultimate movie experience in town.
                </p>
                <span className="text-amber-400 dark:text-amber-400 flex items-center text-sm font-medium">
                  Learn More <ChevronRight size={16} className="ml-1" />
                </span>
              </div>
            </motion.div> */}

            {/* Infrastructure */}
            <motion.div
              className="group relative overflow-hidden rounded-2xl h-96 cursor-pointer"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={scaleUpVariants}
              onClick={() => scrollToSection("infrastructure")}
            >
              <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/50 to-black/80 z-10"></div>
              <img
                src="https://img.freepik.com/free-photo/great-modern-infrastructure-megapolis_140725-8585.jpg?uid=R158437386&ga=GA1.1.516298163.1739876026&semt=ais_hybrid&w=740"
                alt="Infrastructure Division"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute bottom-0 left-0 right-0 p-6 z-20 transform transition-transform duration-500 group-hover:translate-y-2">
                <div className="bg-amber-500 dark:bg-amber-500 w-10 h-10 rounded-full flex items-center justify-center mb-4">
                  <Construction size={20} className="text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">
                  Infrastructure & Energy Division
                </h3>
                <p className="text-gray-200 mb-4">
                  Building tomorrow with smart infrastructure and clean solar
                  energy.
                </p>
                <span className="text-amber-400 dark:text-amber-400 flex items-center text-sm font-medium">
                  Learn More <ChevronRight size={16} className="ml-1" />
                </span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Steel Section */}
      <section id="steel" className="py-20 bg-white dark:bg-gray-900">
        <div className="container mx-auto px-6">
          <motion.div
            className="text-center mb-16"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeInVariants}
          >
            <span className="inline-block px-4 py-1 bg-amber-100 dark:bg-amber-900/50 text-amber-800 dark:text-amber-300 rounded-full text-sm font-medium mb-4">
              STEEL DIVISION
            </span>

            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900 dark:text-white">
              Premium Quality TMT Bars
            </h2>

            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              Dev Bhoomi TMT Sales Pvt. Ltd. , since 2005, has been delivering
              high-quality steel bars that strengthen the foundation of modern
              India.
            </p>
          </motion.div>

          <motion.div
            className="group bg-white dark:bg-gray-800 rounded-3xl shadow-xl overflow-hidden"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            <div className="flex flex-col lg:flex-row">
              <motion.div
                className="lg:w-3/5 relative h-200 lg:h-150 overflow-hidden"
                variants={fadeInVariants}
              >
                <img
                  src="https://static.wixstatic.com/media/11062b_bb02f705f6b6495bae35d7f1580451aa~mv2.jpeg/v1/crop/x_504,y_0,w_2000,h_2000/fill/w_355,h_355,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/Stacked%20Steel%20Rods.jpeg"
                  alt="Steel Manufacturing"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />

                <div className="absolute top-0 left-0 right-0 bottom-0 bg-gradient-to-r from-black/60 to-transparent"></div>
                <div className="absolute top-4 right-4 sm:top-6 sm:right-6 md:top-8 md:right-8 bg-white dark:bg-gray-500 rounded-xl p-2 sm:p-3 md:p-4 shadow-md">
                  <img
                    src="https://static.wixstatic.com/media/73507c_5e8d49295b0845a39d4d1cd8d3ccca7e~mv2.png/v1/crop/x_50,y_0,w_2965,h_603/fill/w_324,h_59,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/logo.png"
                    alt="Steel Manufacturing Logo"
                    className="h-6 sm:h-7 md:h-8 w-auto"
                  />
                </div>
                <div className="absolute bottom-8 left-8 max-w-sm">
                  <h3 className="text-2xl md:text-3xl font-bold text-white mb-3">
                    State-of-the-Art Manufacturing
                  </h3>
                  <p className="text-gray-200 mb-6">
                    Produced with precision and strength to meet modern
                    construction needs.
                  </p>
                  <button
                    onClick={() => {
                      window.open("https://devbhoomitmt.com", "_blank");
                    }}
                    className="group relative px-4 py-1 md:px-6 md:py-2 bg-gradient-to-r from-indigo-500 to-indigo-700 text-white font-normal md:font-medium tracking-wider text-lg rounded-lg overflow-hidden shadow-xl hover:shadow-indigo-200 transition-all duration-300 transform hover:-translate-y-1 focus:outline-none"
                  >
                    <span className="relative z-10 flex items-center justify-center">
                      Explore Our Process
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5 ml-2 transform group-hover:translate-x-1 transition-transform"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M14 5l7 7m0 0l-7 7m7-7H3"
                        />
                      </svg>
                    </span>
                  </button>
                </div>
              </motion.div>

              <motion.div
                className="lg:w-2/5 p-6 md:p-8 lg:p-12"
                variants={fadeInVariants}
              >
                <h4 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">
                  Why Choose Dev Bhoomi Steel?
                </h4>

                <div className="space-y-6">
                  <div className="bg-gray-50 dark:bg-gray-700 p-3 md:p-6 rounded-2xl hover:shadow-md transition-all duration-300 border border-gray-100 dark:border-gray-600">
                    <div className="flex items-center mb-3">
                      <div className="w-10 h-10 rounded-full bg-indigo-100 dark:bg-indigo-900/50 flex items-center justify-center mr-3">
                        <Award
                          size={20}
                          className="text-indigo-600 dark:text-indigo-400"
                        />
                      </div>
                      <h5 className="font-bold text-lg text-gray-900 dark:text-white">
                        Superior Strength
                      </h5>
                    </div>
                    <p className="text-gray-600 dark:text-gray-300 mb-4">
                      Built to endure, our TMT bars offer exceptional tensile
                      strength and durability.
                    </p>
                  </div>

                  <div className="bg-gray-50 dark:bg-gray-700 p-3 md:p-6 rounded-2xl hover:shadow-md transition-all duration-300 border border-gray-100 dark:border-gray-600">
                    <div className="flex items-center mb-3">
                      <div className="w-10 h-10 rounded-full bg-indigo-100 dark:bg-indigo-900/50 flex items-center justify-center mr-3">
                        <Star
                          size={20}
                          className="text-indigo-600 dark:text-indigo-400"
                        />
                      </div>
                      <h5 className="font-bold text-lg text-gray-900 dark:text-white">
                        Certified Quality
                      </h5>
                    </div>
                    <p className="text-gray-600 dark:text-gray-300 mb-4">
                      Our steel is ISO-certified and tested rigorously to meet
                      the highest standards.
                    </p>
                  </div>

                  <div className="bg-gray-50 dark:bg-gray-700 p-3 md:p-6 rounded-2xl hover:shadow-md transition-all duration-300 border border-gray-100 dark:border-gray-600">
                    <div className="flex items-center mb-3">
                      <div className="w-10 h-10 rounded-full bg-indigo-100 dark:bg-indigo-900/50 flex items-center justify-center mr-3">
                        <Building
                          size={20}
                          className="text-indigo-600 dark:text-indigo-400"
                        />
                      </div>
                      <h5 className="font-bold text-lg text-gray-900 dark:text-white">
                        Infrastructure Backbone
                      </h5>
                    </div>
                    <p className="text-gray-600 dark:text-gray-300 mb-4">
                      Supplying top-grade steel to major infrastructure projects
                      across North India.
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Automobile Section */}
      <section
        id="automobile"
        className="pt-20 md:pt-32 bg-white dark:bg-gray-900"
      >
        <div className="container mx-auto px-6">
          <motion.div
            className="text-center mb-16"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeInVariants}
          >
            <span className="inline-block px-4 py-1 bg-amber-100 dark:bg-amber-900/50 text-amber-800 dark:text-amber-300 rounded-full text-sm font-medium mb-4">
              AUTOMOBILE DIVISION
            </span>

            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900 dark:text-white">
              Authorized Dealerships
            </h2>

            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              Dev Bhoomi Group Companies has authorized dealerships for Skoda,
              Hyundai, and Tata Motors in Himachal Pradesh. The Group will
              soon be venturing into Electric Cars.
            </p>
          </motion.div>

          {/* Skoda Section */}
          <div id="skoda" className="mb-24">
            <motion.div
              className="group bg-white dark:bg-gray-800 rounded-3xl shadow-xl overflow-hidden"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
            >
              <div className="flex flex-col lg:flex-row">
                <motion.div
                  className="lg:w-3/5 relative h-96 lg:h-auto overflow-hidden"
                  variants={fadeInVariants}
                >
                  <img
                    src="https://singleinterface-timeline-images.s3.ap-south-1.amazonaws.com/images/67021/fb_post_67021_2025_04_23_00_01_14_230147504432_1112898804205544.jpg"
                    alt="Skoda Dealership"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute top-0 left-0 right-0 bottom-0 bg-gradient-to-r from-black/60 to-transparent"></div>
                  <div className="absolute top-4 right-4 sm:top-6 sm:right-6 md:top-8 md:right-8 bg-white dark:bg-gray-500 rounded-xl p-2 sm:p-3 md:p-4 shadow-md">
                    <img
                      src="https://skodalive.co.in/SkodaMenu/images/logo.png"
                      alt="Skoda Logo"
                      className="h-6 sm:h-7 md:h-8 w-auto"
                    />
                  </div>
                  <div className="absolute bottom-8 left-8 max-w-sm">
                    <h3 className="text-3xl font-bold text-white mb-3">
                      Skoda Dealership
                    </h3>
                    <p className="text-gray-200 mb-6">
                      Experience luxury and performance with our premium Skoda
                      vehicles.
                    </p>
                    <button
                      onClick={() => {
                        window.open(
                          "https://dealers.skoda-auto.co.in/dev-bhoomi-skoda-showroom-and-service-dev-bhoomi-motors-pvt-ltd-car-dealer-gutkar-mandi-67723/Home?utm_source=locator&utm_medium=googleplaces",
                          "_blank"
                        );
                      }}
                      className="group relative px-4 py-1 md:px-6 md:py-2 bg-gradient-to-r from-green-500 to-green-700 text-white font-normal md:font-medium tracking-wider text-lg rounded-lg overflow-hidden shadow-xl hover:shadow-green-200 transition-all duration-300 transform hover:-translate-y-1 focus:outline-none"
                    >
                      <span className="relative z-10 flex items-center justify-center">
                        Visit Showroom
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5 ml-2 transform group-hover:translate-x-1 transition-transform"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M14 5l7 7m0 0l-7 7m7-7H3"
                          />
                        </svg>
                      </span>
                    </button>
                  </div>
                </motion.div>

                <motion.div
                  className="lg:w-2/5 p-6 md:p-8 lg:p-12"
                  variants={fadeInVariants}
                >
                  <h4 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">
                    Featured Models
                  </h4>

                  <div className="space-y-6">
                    <div className="bg-gray-50 dark:bg-gray-700 p-3 md:p-6 rounded-2xl hover:shadow-md transition-all duration-300 border border-gray-100 dark:border-gray-600">
                      <div className="flex items-center mb-3">
                        <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center mr-3">
                          <Car
                            size={20}
                            className="text-green-600 dark:text-green-300"
                          />
                        </div>
                        <div className="flex flex-col">
                          <h5 className="font-bold text-lg text-gray-900 dark:text-white">
                            Skoda Octavia
                          </h5>
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            Starting from ₹26 L
                          </span>
                        </div>
                      </div>

                      <p className="text-gray-600 dark:text-gray-300 mb-4">
                        Elegance meets performance in this sophisticated sedan
                        with advanced features.
                      </p>
                    </div>

                    <div className="bg-gray-50 dark:bg-gray-700 p-3 md:p-6 rounded-2xl hover:shadow-md transition-all duration-300 border border-gray-100 dark:border-gray-600">
                      <div className="flex items-center mb-3">
                        <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center mr-3">
                          <Car
                            size={20}
                            className="text-green-600 dark:text-green-300"
                          />
                        </div>
                        <div className="flex flex-col">
                          <h5 className="font-bold text-lg text-gray-900 dark:text-white">
                            Skoda Superb
                          </h5>
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            Starting from ₹32 L
                          </span>
                        </div>
                      </div>

                      <p className="text-gray-600 dark:text-gray-300 mb-4">
                        Luxury redefined with superior comfort, cutting-edge
                        technology, and elegant design.
                      </p>
                    </div>

                    <div className="bg-gray-50 dark:bg-gray-700 p-3 md:p-6 rounded-2xl hover:shadow-md transition-all duration-300 border border-gray-100 dark:border-gray-600">
                      <div className="flex items-center mb-3">
                        <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center mr-3">
                          <Car
                            size={20}
                            className="text-green-600 dark:text-green-300"
                          />
                        </div>
                        <div className="flex flex-col">
                          <h5 className="font-bold text-lg text-gray-900 dark:text-white">
                            Skoda Kodiaq
                          </h5>
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            Starting from ₹35 L
                          </span>
                        </div>
                      </div>

                      <p className="text-gray-600 dark:text-gray-300 mb-4">
                        Powerful SUV experience combining versatility, safety,
                        and premium features for the whole family.
                      </p>
                    </div>
                  </div>

                  <div className="mt-8">
                    <h4 className="text-lg font-bold mb-4 text-gray-900 dark:text-white">
                      Premium Services
                    </h4>
                    <div className="flex flex-wrap gap-3">
                      <span className="inline-flex items-center px-3 py-1 rounded-full bg-green-50 dark:bg-green-900/50 text-green-700 dark:text-green-300 text-sm font-medium">
                        <Award size={14} className="mr-1" />
                        4-Year Warranty
                      </span>
                      <span className="inline-flex items-center px-3 py-1 rounded-full bg-green-50 dark:bg-green-900/50 text-green-700 dark:text-green-300 text-sm font-medium">
                        <Clock size={14} className="mr-1" />
                        24/7 Roadside Assistance
                      </span>
                      <span className="inline-flex items-center px-3 py-1 rounded-full bg-green-50 dark:bg-green-900/50 text-green-700 dark:text-green-300 text-sm font-medium">
                        <Star size={14} className="mr-1" />
                        Premium Maintenance
                      </span>
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>

          {/* Hyundai Section */}
          <div id="hyundai" className="mb-20">
            <motion.div
              className="group bg-white dark:bg-gray-800 rounded-3xl shadow-xl overflow-hidden"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
            >
              <div className="flex flex-col lg:flex-row-reverse">
                <motion.div
                  className="lg:w-3/5 relative h-96 lg:h-auto overflow-hidden"
                  variants={fadeInVariants}
                >
                  <img
                    src="https://images.unsplash.com/photo-1575090536203-2a6193126514?q=80&w=2071&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
                    alt="Hyundai Dealership"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute top-0 left-0 right-0 bottom-0 bg-gradient-to-l from-black/60 to-transparent"></div>
                  <div className="absolute top-4 right-4 sm:top-6 sm:right-6 md:top-8 md:right-8 bg-white dark:bg-gray-500 rounded-xl p-2 sm:p-3 md:p-4 shadow-md">
                    <div className="h-6 sm:h-7 md:h-8 w-auto flex items-center justify-center">
                      <span className="text-blue-600 font-bold">
                        DEV BHOOMI HYUNDAI
                      </span>
                    </div>
                  </div>

                  <div className="absolute bottom-8 right-8 max-w-sm text-right">
                    <h3 className="text-2xl md:text-3xl font-bold text-white mb-3">
                      Hyundai Dealership
                    </h3>
                    <p className="text-gray-200 mb-6">
                      Innovation and reliability with our modern Hyundai lineup.
                    </p>
                    <button
                      onClick={() => {
                        window.open(
                          "https://devbhoomi.hyundaimotor.in/",
                          "_blank"
                        );
                      }}
                      className="group relative px-4 py-1 md:px-6 md:py-2 bg-gradient-to-r from-blue-500 to-blue-700 text-white font-normal md:font-medium tracking-wider text-lg rounded-lg overflow-hidden shadow-xl hover:shadow-blue-200 transition-all duration-300 transform hover:-translate-y-1 focus:outline-none"
                    >
                      <span className="relative z-10 flex items-center justify-center">
                        Visit Showroom
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5 ml-2 transform group-hover:translate-x-1 transition-transform"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M14 5l7 7m0 0l-7 7m7-7H3"
                          />
                        </svg>
                      </span>
                    </button>
                  </div>
                </motion.div>

                <motion.div
                  className="lg:w-2/5 p-6 md:p-8 lg:p-12"
                  variants={fadeInVariants}
                >
                  <h4 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">
                    Featured Models
                  </h4>

                  <div className="space-y-6">
                    <div className="bg-gray-50 dark:bg-gray-700 p-3 md:p-6 rounded-2xl hover:shadow-md transition-all duration-300 border border-gray-100 dark:border-gray-600">
                      <div className="flex items-center mb-3">
                        <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center mr-3">
                          <Car
                            size={20}
                            className="text-blue-600 dark:text-blue-300"
                          />
                        </div>
                        <div className="flex flex-col">
                          <h5 className="font-bold text-lg text-gray-900 dark:text-white">
                            Hyundai Creta
                          </h5>
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            Starting from ₹10 L
                          </span>
                        </div>
                      </div>
                      <p className="text-gray-600 dark:text-gray-300 mb-4">
                        Perfect compact SUV combining style, technology, and
                        convenience for urban adventures.
                      </p>
                    </div>

                    <div className="bg-gray-50 dark:bg-gray-700 p-3 md:p-6 rounded-2xl hover:shadow-md transition-all duration-300 border border-gray-100 dark:border-gray-600">
                      <div className="flex items-center mb-3">
                        <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center mr-3">
                          <Car
                            size={20}
                            className="text-blue-600 dark:text-blue-300"
                          />
                        </div>
                        <div className="flex flex-col">
                          <h5 className="font-bold text-lg text-gray-900 dark:text-white">
                            Hyundai i20
                          </h5>
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            Starting from ₹7 L
                          </span>
                        </div>
                      </div>
                      <p className="text-gray-600 dark:text-gray-300 mb-4">
                        Premium hatchback with advanced features, stylish
                        design, and exceptional driving dynamics.
                      </p>
                    </div>

                    <div className="bg-gray-50 dark:bg-gray-700 p-3 md:p-6 rounded-2xl hover:shadow-md transition-all duration-300 border border-gray-100 dark:border-gray-600">
                      <div className="flex items-center mb-3">
                        <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center mr-3">
                          <Car
                            size={20}
                            className="text-blue-600 dark:text-blue-300"
                          />
                        </div>
                        <div className="flex flex-col">
                          <h5 className="font-bold text-lg text-gray-900 dark:text-white">
                            Hyundai Tucson
                          </h5>
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            Starting from ₹22 L
                          </span>
                        </div>
                      </div>
                      <p className="text-gray-600 dark:text-gray-300 mb-4">
                        Advanced technology SUV with premium safety features and
                        sophisticated driving experience.
                      </p>
                    </div>
                  </div>

                  <div className="mt-8">
                    <h4 className="text-lg font-bold mb-4 text-gray-900 dark:text-white">
                      Customer Benefits
                    </h4>
                    <div className="flex flex-wrap gap-3">
                      <span className="inline-flex items-center px-3 py-1 rounded-full bg-blue-50 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 text-sm font-medium">
                        <Award size={14} className="mr-1" />
                        5-Year Warranty
                      </span>
                      <span className="inline-flex items-center px-3 py-1 rounded-full bg-blue-50 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 text-sm font-medium">
                        <Clock size={14} className="mr-1" />
                        Free Service Camps
                      </span>
                      <span className="inline-flex items-center px-3 py-1 rounded-full bg-blue-50 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 text-sm font-medium">
                        <Star size={14} className="mr-1" />
                        Loyalty Benefits
                      </span>
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>

          {/* Tata Motors Section */}
          <div id="tata-motors" className="mb-20">
            <motion.div
              className="group bg-white dark:bg-gray-800 rounded-3xl shadow-xl overflow-hidden"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
            >
              <div className="flex flex-col lg:flex-row">
                <motion.div
                  className="lg:w-3/5 relative h-96 lg:h-auto overflow-hidden"
                  variants={fadeInVariants}
                >
                  <img
                    src="https://img.freepik.com/free-photo/where-i-need-sign-female-customer-modern-stylish-bearded-businessman-automobile-saloon_146671-16730.jpg"
                    alt="Automotive showroom"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute top-0 left-0 right-0 bottom-0 bg-gradient-to-r from-black/60 to-transparent"></div>
                  <div className="absolute top-4 right-4 sm:top-6 sm:right-6 md:top-8 md:right-8 bg-white dark:bg-gray-500 rounded-xl p-2 sm:p-3 md:p-4 shadow-md">
                    <div className="h-6 sm:h-7 md:h-8 w-auto flex items-center justify-center">
                      <span className="text-blue-600 font-bold">
                        TATA MOTORS
                      </span>
                    </div>
                  </div>

                  <div className="absolute bottom-8 left-8 max-w-sm">
                    <h3 className="text-2xl md:text-3xl font-bold text-white mb-3">
                      Tata Motors Dealership
                    </h3>
                    <p className="text-gray-200 mb-6">
                      Explore our Tata Motors lineup, the latest addition to our automotive family.
                    </p>
                    <button
                      onClick={() => {
                        window.open(
                          "https://share.google/TT9N9QzuQyIgGNcRw",
                          "_blank"
                        );
                      }}
                      className="group relative px-4 py-1 md:px-6 md:py-2 bg-gradient-to-r from-blue-500 to-blue-700 text-white font-normal md:font-medium tracking-wider text-lg rounded-lg overflow-hidden shadow-xl hover:shadow-blue-200 transition-all duration-300 transform hover:-translate-y-1 focus:outline-none"
                    >
                      <span className="relative z-10 flex items-center justify-center">
                        Visit Showroom
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5 ml-2 transform group-hover:translate-x-1 transition-transform"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M14 5l7 7m0 0l-7 7m7-7H3"
                          />
                        </svg>
                      </span>
                    </button>
                  </div>
                </motion.div>

                <motion.div
                  className="lg:w-2/5 p-6 md:p-8 lg:p-12"
                  variants={fadeInVariants}
                >
                  <h4 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">
                    Featured Models
                  </h4>

                  <div className="space-y-6">
                    <div className="bg-gray-50 dark:bg-gray-700 p-3 md:p-6 rounded-2xl hover:shadow-md transition-all duration-300 border border-gray-100 dark:border-gray-600">
                      <div className="flex items-center mb-3">
                        <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center mr-3">
                          <Car
                            size={20}
                            className="text-blue-600 dark:text-blue-300"
                          />
                        </div>
                        <div className="flex flex-col">
                          <h5 className="font-bold text-lg text-gray-900 dark:text-white">
                            Tata Nexon
                          </h5>
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            Compact SUV
                          </span>
                        </div>
                      </div>
                      <p className="text-gray-600 dark:text-gray-300 mb-4">
                        Discover the Tata Nexon and explore the available variants with our showroom team.
                      </p>
                    </div>

                    <div className="bg-gray-50 dark:bg-gray-700 p-3 md:p-6 rounded-2xl hover:shadow-md transition-all duration-300 border border-gray-100 dark:border-gray-600">
                      <div className="flex items-center mb-3">
                        <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center mr-3">
                          <Car
                            size={20}
                            className="text-blue-600 dark:text-blue-300"
                          />
                        </div>
                        <div className="flex flex-col">
                          <h5 className="font-bold text-lg text-gray-900 dark:text-white">
                            Tata Punch
                          </h5>
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            SUV
                          </span>
                        </div>
                      </div>
                      <p className="text-gray-600 dark:text-gray-300 mb-4">
                        Explore the Tata Punch and find a variant that suits your everyday journeys.
                      </p>
                    </div>

                    <div className="bg-gray-50 dark:bg-gray-700 p-3 md:p-6 rounded-2xl hover:shadow-md transition-all duration-300 border border-gray-100 dark:border-gray-600">
                      <div className="flex items-center mb-3">
                        <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center mr-3">
                          <Car
                            size={20}
                            className="text-blue-600 dark:text-blue-300"
                          />
                        </div>
                        <div className="flex flex-col">
                          <h5 className="font-bold text-lg text-gray-900 dark:text-white">
                            Tata Harrier
                          </h5>
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            SUV
                          </span>
                        </div>
                      </div>
                      <p className="text-gray-600 dark:text-gray-300 mb-4">
                        Discover the Tata Harrier and speak with our showroom team about its features and available variants.
                      </p>
                    </div>
                  </div>

                  <div className="mt-8">
                    <h4 className="text-lg font-bold mb-4 text-gray-900 dark:text-white">
                      Explore With Us
                    </h4>
                    <div className="flex flex-wrap gap-3">
                      <span className="inline-flex items-center px-3 py-1 rounded-full bg-blue-50 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 text-sm font-medium">
                        <Award size={14} className="mr-1" />
                        Model Range
                      </span>
                      <span className="inline-flex items-center px-3 py-1 rounded-full bg-blue-50 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 text-sm font-medium">
                        <Clock size={14} className="mr-1" />
                        Showroom Enquiries
                      </span>
                      <span className="inline-flex items-center px-3 py-1 rounded-full bg-blue-50 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 text-sm font-medium">
                        <Star size={14} className="mr-1" />
                        Variant Information
                      </span>
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>

          {/* Electric Future */}
          <motion.div
            className="mt-24"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeInVariants}
          >
            <div className="bg-gradient-to-br from-gray-900 to-gray-800 dark:from-gray-800 dark:to-gray-900 rounded-3xl overflow-hidden shadow-2xl relative">
              <div className="absolute top-0 right-0 w-1/2 h-full opacity-10">
                <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
                  <path
                    fill="#FFFFFF"
                    d="M43.2,-69.2C53.9,-62.3,59.4,-46.6,65.8,-31.9C72.3,-17.2,79.7,-3.4,78.3,9.4C76.9,22.2,66.6,34,55.6,42.8C44.6,51.6,32.8,57.4,20.4,60.5C8,63.7,-5.1,64.1,-17.9,61.2C-30.7,58.3,-43.2,52,-52.9,42.1C-62.6,32.3,-69.4,18.9,-72.8,3.8C-76.1,-11.3,-76,-28,-68.3,-39.8C-60.6,-51.5,-45.3,-58.3,-31.4,-63.3C-17.5,-68.3,-5,-71.5,7.8,-72.8C20.6,-74.1,32.4,-76.1,43.2,-69.2Z"
                    transform="translate(100 100)"
                  />
                </svg>
              </div>

              <div className="py-16 px-8 md:p-16 text-white relative z-10">
                <div className="flex flex-col lg:flex-row items-center">
                  <div className="lg:w-1/2 lg:pr-12 mb-10 lg:mb-0">
                    <span className="inline-block px-4 py-1 bg-amber-500 text-white rounded-full text-sm font-medium mb-6">
                      COMING SOON
                    </span>

                    <h3 className="text-3xl md:text-4xl font-bold mb-6">
                      The Future is Electric
                    </h3>

                    {/* Company Box */}
                    <div className="bg-gray-900/60 border-l-4 border-amber-500 p-5 rounded-xl shadow-md mb-8">
                      <h4 className="text-xl font-bold text-amber-400 uppercase tracking-widest">
                        Dev Bhoomi Electrical Car Pvt. Ltd.
                      </h4>
                      <p className="text-gray-400 text-sm mt-2">
                        Powered by Dev Bhoomi Group Companies — Eco-smart
                        vehicles for a cleaner tomorrow.
                      </p>
                    </div>

                    <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                      Dev Bhoomi Group Companies is committed to a sustainable
                      future. We're excited to announce our upcoming venture
                      into Electric Vehicles, bringing the latest eco-friendly
                      automotive technology to Himachal Pradesh.
                    </p>

                    <div className="mb-8">
                      <div className="flex items-center mb-4">
                        <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center mr-4">
                          <Car size={20} className="text-amber-400" />
                        </div>
                        <div>
                          <h4 className="font-bold text-white">
                            Zero Emission Vehicles
                          </h4>
                          <p className="text-gray-400">
                            Environmentally conscious transportation for a
                            cleaner tomorrow.
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center">
                        <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center mr-4">
                          <Clock size={20} className="text-amber-400" />
                        </div>
                        <div>
                          <h4 className="font-bold text-white">
                            Launch in 2025
                          </h4>
                          <p className="text-gray-400">
                            Be the first to experience our EV lineup
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="lg:w-1/2">
                    <img
                      src="https://www.gettruepower.in/assets/website/blogs/8.png"
                      alt="Electric Vehicle Concept"
                      className="rounded-xl shadow-2xl"
                    />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Tourism Section */}
      <section
        id="tourism"
        className="pt-20 md:pt-32 pb-20 bg-white dark:bg-gray-900"
      >
        <div className="container mx-auto px-6">
          <motion.div
            className="text-center mb-16"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeInVariants}
          >
            <span className="inline-block px-4 py-1 bg-amber-100 dark:bg-amber-900 text-amber-800 dark:text-amber-200 rounded-full text-sm font-medium mb-4">
              TOURISM & HOSPITALITY
            </span>
            <h2 className="text-4xl md:text-5xl font-bold mb-6 dark:text-white">
              The Gulmohar Grand
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              Dev Bhoomi Group Companies premium hotel venture offering luxury
              stays and international cuisines under one roof since 2019.
            </p>
          </motion.div>

          <motion.div
            className="group bg-white dark:bg-gray-800 rounded-3xl shadow-xl overflow-hidden"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            <div className="flex flex-col lg:flex-row">
              <motion.div
                className="lg:w-3/5 relative h-96 lg:h-auto overflow-hidden"
                variants={fadeInVariants}
              >
                <img
                  src="https://ik.imagekit.io/jncw2kb8u/hotel-in-una1.jpg?updatedAt=1745481996268"
                  alt="The Gulmohar Grand Hotel"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />

                <div className="absolute top-0 left-0 right-0 bottom-0 bg-gradient-to-r from-black/60 to-transparent"></div>
                <div className="absolute top-4 right-4 sm:top-6 sm:right-6 md:top-8 md:right-8 bg-white dark:bg-gray-500 rounded-xl p-2 sm:p-3 md:p-4 shadow-md">
                  <img
                    src="https://gulmohargrandhotel.com/wp-content/uploads/2019/11/pre2.jpg"
                    alt="Gulmohar Grand Hotel Logo"
                    className="h-6 sm:h-7 md:h-8 w-auto"
                  />
                </div>
                <div className="absolute bottom-8 left-8 max-w-sm">
                  <h3 className="text-2xl md:text-3xl font-bold text-white mb-3">
                    Luxury Experience
                  </h3>
                  <p className="text-gray-200 mb-6">
                    Exceptional hospitality services with world-class amenities
                    and fine dining.
                  </p>
                  <button
                    onClick={() => {
                      window.open("https://thegulmohargrand.com", "_blank");
                    }}
                    className="group relative px-6 py-2 bg-gradient-to-r from-amber-500 to-amber-700 text-white font-medium tracking-wider text-lg rounded-lg overflow-hidden shadow-xl hover:shadow-amber-200 transition-all duration-300 transform hover:-translate-y-1 focus:outline-none"
                  >
                    <span className="relative z-10 flex items-center justify-center">
                      Book Your Stay
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5 ml-2 transform group-hover:translate-x-1 transition-transform"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M14 5l7 7m0 0l-7 7m7-7H3"
                        />
                      </svg>
                    </span>
                  </button>
                </div>
              </motion.div>

              <motion.div
                className="lg:w-2/5 p-6 md:p-8 lg:p-12 dark:text-white"
                variants={fadeInVariants}
              >
                <h4 className="text-2xl font-bold mb-6">Premium Services</h4>
                <div className="space-y-6">
                  <div className="bg-gray-50 dark:bg-gray-700 p-3 md:p-6 rounded-2xl hover:shadow-md transition-all duration-300 border border-gray-100 dark:border-gray-600">
                    <div className="flex items-center mb-3">
                      <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900 flex items-center justify-center mr-3">
                        <Award
                          size={20}
                          className="text-amber-600 dark:text-amber-300"
                        />
                      </div>
                      <h5 className="font-bold text-lg">Luxury Suites</h5>
                    </div>
                    <p className="text-gray-600 dark:text-gray-300 mb-4">
                      Beautifully designed rooms with panoramic views and modern
                      amenities.
                    </p>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-700 p-3 md:p-6 rounded-2xl hover:shadow-md transition-all duration-300 border border-gray-100 dark:border-gray-600">
                    <div className="flex items-center mb-3">
                      <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900 flex items-center justify-center mr-3">
                        <Award
                          size={20}
                          className="text-amber-600 dark:text-amber-300"
                        />
                      </div>
                      <h5 className="font-bold text-lg">Fine Dining</h5>
                    </div>
                    <p className="text-gray-600 dark:text-gray-300 mb-4">
                      Multiple international cuisines curated by expert chefs.
                    </p>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-700 p-3 md:p-6 rounded-2xl hover:shadow-md transition-all duration-300 border border-gray-100 dark:border-gray-600">
                    <div className="flex items-center mb-3">
                      <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900 flex items-center justify-center mr-3">
                        <Award
                          size={20}
                          className="text-amber-600 dark:text-amber-300"
                        />
                      </div>
                      <h5 className="font-bold text-lg">Wellness & Spa</h5>
                    </div>
                    <p className="text-gray-600 dark:text-gray-300 mb-4">
                      Premium spa and wellness facilities for complete
                      relaxation.
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      <section id="entertainment" className=" bg-white dark:bg-gray-900">
        <div className="container mx-auto pb-8 px-6">
          <motion.div
            className="text-center mb-16"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeInVariants}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6 dark:text-white">
              Grand Cinemaz
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              Dev Bhoomi Group Companies premium multiplex experience bringing
              blockbuster movies and immersive entertainment to audiences since
              2020.
            </p>
          </motion.div>

          <motion.div
            className="group bg-white dark:bg-gray-800 rounded-3xl shadow-xl overflow-hidden"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            <div className="flex flex-col lg:flex-row">
              <motion.div
                className="lg:w-2/5 p-6 md:p-8 lg:p-12 dark:text-white"
                variants={fadeInVariants}
              >
                <h4 className="text-2xl font-bold mb-6">Cinema Features</h4>
                <div className="space-y-6">
                  <div className="bg-gray-50 dark:bg-gray-700 p-3 md:p-6 rounded-2xl hover:shadow-md transition-all duration-300 border border-gray-100 dark:border-gray-600">
                    <div className="flex items-center mb-3">
                      <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900 flex items-center justify-center mr-3">
                        <Film
                          size={20}
                          className="text-red-600 dark:text-red-300"
                        />
                      </div>
                      <h5 className="font-bold text-lg">2K Projection</h5>
                    </div>
                    <p className="text-gray-600 dark:text-gray-300 mb-4">
                      Ultra-high definition screens with cutting-edge laser
                      projection technology.
                    </p>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-700 p-3 md:p-6 rounded-2xl hover:shadow-md transition-all duration-300 border border-gray-100 dark:border-gray-600">
                    <div className="flex items-center mb-3">
                      <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900 flex items-center justify-center mr-3">
                        <Volume2
                          size={20}
                          className="text-red-600 dark:text-red-300"
                        />
                      </div>
                      <h5 className="font-bold text-lg">Dolby Atmos Sound</h5>
                    </div>
                    <p className="text-gray-600 dark:text-gray-300 mb-4">
                      Immersive multi-dimensional audio experience for ultimate
                      cinematic enjoyment.
                    </p>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-700 p-3 md:p-6 rounded-2xl hover:shadow-md transition-all duration-300 border border-gray-100 dark:border-gray-600">
                    <div className="flex items-center mb-3">
                      <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900 flex items-center justify-center mr-3">
                        <Coffee
                          size={20}
                          className="text-red-600 dark:text-red-300"
                        />
                      </div>
                      <h5 className="font-bold text-lg">Premium Snack Bar</h5>
                    </div>
                    <p className="text-gray-600 dark:text-gray-300 mb-4">
                      Gourmet snacks, beverages, and specialty food options for
                      movie enthusiasts.
                    </p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                className="lg:w-3/5 relative h-96 lg:h-auto overflow-hidden"
                variants={fadeInVariants}
              >
                <img
                  src="https://ik.imagekit.io/zfayt6inj/view-3d-cinema-theatre-room.jpg?updatedAt=1746045914638"
                  alt="Grand Cinemaz Multiplex"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />

                <div className="absolute top-0 left-0 right-0 bottom-0 bg-gradient-to-l from-black/60 to-transparent"></div>
                <div className="absolute top-4 left-4 sm:top-6 sm:left-6 md:top-8 md:left-8 bg-white dark:bg-gray-500 rounded-xl p-2 sm:p-3 md:p-4 shadow-md">
                  <img
                    src="https://ik.imagekit.io/7mw0on9oya/grand-cinemaz.jpeg?updatedAt=1745998862491"
                    alt="Grand Cinemaz Logo"
                    className="h-6 sm:h-7 md:h-8 w-auto"
                  />
                </div>
                <div className="absolute bottom-8 right-8 max-w-sm text-right">
                  <h3 className="text-2xl md:text-3xl font-bold text-white mb-3">
                    Premium Movie Experience
                  </h3>
                  <p className="text-gray-200 mb-6">
                    State-of-the-art screens with immersive sound systems and
                    luxury seating.
                  </p>
                  <button
                    onClick={() => {
                      window.open("https://grandcinemaz.com", "_blank");
                    }}
                    className="group relative px-6 py-2 bg-gradient-to-r from-red-500 to-red-700 text-white font-medium tracking-wider text-lg rounded-lg overflow-hidden shadow-xl hover:shadow-red-200 transition-all duration-300 transform hover:-translate-y-1 focus:outline-none mt-4"
                  >
                    <span className="relative z-10 flex items-center justify-center">
                      Book Tickets
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5 ml-2 transform group-hover:translate-x-1 transition-transform"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M14 5l7 7m0 0l-7 7m7-7H3"
                        />
                      </svg>
                    </span>
                  </button>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Infrastructure Section */}
      <section
        id="infrastructure"
        className="pt-20 md:pt-32 bg-white dark:bg-gray-900"
      >
        <div className="container mx-auto px-6">
          <motion.div
            className="text-center mb-16"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeInVariants}
          >
            <span className="inline-block px-4 py-1 bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-blue-300 rounded-full text-sm font-medium mb-4">
              INFRASTRUCTURE DIVISION
            </span>

            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900 dark:text-white">
              Building a Sustainable Future
            </h2>

            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              Dev Bhoomi Group Companies is pioneering sustainable
              infrastructure development in Himachal Pradesh through innovative
              solar power solutions and premium real estate projects.
            </p>
          </motion.div>

          {/* Solar Power Section */}
          <div id="solar" className="mb-24">
            <motion.div
              className="group bg-white dark:bg-gray-800 rounded-3xl shadow-xl overflow-hidden"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
            >
              <div className="flex flex-col lg:flex-row">
                <motion.div
                  className="lg:w-3/5 relative h-96 lg:h-auto overflow-hidden"
                  variants={fadeInVariants}
                >
                  <img
                    src="https://images.pexels.com/photos/9799727/pexels-photo-9799727.jpeg?auto=compress&cs=tinysrgb&w=6000"
                    alt="Solar Power Solutions"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute top-0 left-0 right-0 bottom-0 bg-gradient-to-r from-black/60 to-transparent"></div>
                  <div className="absolute top-4 right-4 sm:top-6 sm:right-6 md:top-8 md:right-8 bg-white dark:bg-gray-500 rounded-xl p-2 sm:p-3 md:p-4 shadow-md">
                    <div className="h-6 sm:h-7 md:h-8 w-auto flex items-center justify-center">
                      <span className="text-yellow-500 font-bold">
                        FDL SOLAR POWER
                      </span>
                    </div>
                  </div>
                  <div className="absolute bottom-8 left-8 max-w-sm">
                    <h3 className="text-3xl font-bold text-white mb-3">
                      Solar Power Solutions
                    </h3>
                    <p className="text-gray-200 mb-6">
                      Harnessing clean energy for a sustainable future in the
                      Himalayan region.
                    </p>
                    <button
                      onClick={() => {
                        window.open("https://fdlsolarpower.com", "_blank");
                      }}
                      className="group relative px-4 py-1 md:px-6 md:py-2 bg-gradient-to-r from-yellow-500 to-yellow-700 text-white font-normal md:font-medium tracking-wider text-lg rounded-lg overflow-hidden shadow-xl hover:shadow-yellow-200 transition-all duration-300 transform hover:-translate-y-1 focus:outline-none"
                    >
                      <span className="relative z-10 flex items-center justify-center">
                        Learn More
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5 ml-2 transform group-hover:translate-x-1 transition-transform"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M14 5l7 7m0 0l-7 7m7-7H3"
                          />
                        </svg>
                      </span>
                    </button>
                  </div>
                </motion.div>

                <motion.div
                  className="lg:w-2/5 p-6 md:p-8 lg:p-12"
                  variants={fadeInVariants}
                >
                  <h4 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">
                    Our Solar Services
                  </h4>

                  <div className="space-y-6">
                    <div className="bg-gray-50 dark:bg-gray-700 p-3 md:p-6 rounded-2xl hover:shadow-md transition-all duration-300 border border-gray-100 dark:border-gray-600">
                      <div className="flex items-center mb-3">
                        <div className="w-10 h-10 rounded-full bg-yellow-100 dark:bg-yellow-900 flex items-center justify-center mr-3">
                          <Sun
                            size={20}
                            className="text-yellow-600 dark:text-yellow-300"
                          />
                        </div>
                        <h5 className="font-bold text-lg text-gray-900 dark:text-white">
                          Residential Solar
                        </h5>
                      </div>
                      <p className="text-gray-600 dark:text-gray-300 mb-4">
                        Custom solar solutions for homes with battery backup
                        options and grid integration.
                      </p>
                    </div>

                    <div className="bg-gray-50 dark:bg-gray-700 p-3 md:p-6 rounded-2xl hover:shadow-md transition-all duration-300 border border-gray-100 dark:border-gray-600">
                      <div className="flex items-center mb-3">
                        <div className="w-10 h-10 rounded-full bg-yellow-100 dark:bg-yellow-900 flex items-center justify-center mr-3">
                          <Sun
                            size={20}
                            className="text-yellow-600 dark:text-yellow-300"
                          />
                        </div>
                        <h5 className="font-bold text-lg text-gray-900 dark:text-white">
                          Commercial Projects
                        </h5>
                      </div>
                      <p className="text-gray-600 dark:text-gray-300 mb-4">
                        Large-scale solar installations for businesses, hotels,
                        and institutions with power purchase agreements.
                      </p>
                    </div>

                    <div className="bg-gray-50 dark:bg-gray-700 p-3 md:p-6 rounded-2xl hover:shadow-md transition-all duration-300 border border-gray-100 dark:border-gray-600">
                      <div className="flex justify-between items-center mb-3">
                        <div className="flex items-center mb-3">
                          <div className="w-10 h-10 rounded-full bg-yellow-100 dark:bg-yellow-900 flex items-center justify-center mr-3">
                            <Sun
                              size={20}
                              className="text-yellow-600 dark:text-yellow-300"
                            />
                          </div>
                          <h5 className="font-bold text-lg text-gray-900 dark:text-white">
                            Solar Farms
                          </h5>
                        </div>{" "}
                      </div>
                      <p className="text-gray-600 dark:text-gray-300 mb-4">
                        Utility-scale solar development with advanced tracking
                        systems optimized for Himalayan terrain.
                      </p>
                    </div>
                  </div>

                  <div className="mt-8">
                    <h4 className="text-lg font-bold mb-4 text-gray-900 dark:text-white">
                      Key Benefits
                    </h4>
                    <div className="flex flex-wrap gap-3">
                      <span className="inline-flex items-center px-3 py-1 rounded-full bg-yellow-50 dark:bg-yellow-900/50 text-yellow-700 dark:text-yellow-300 text-sm font-medium">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4 mr-1"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13 10V3L4 14h7v7l9-11h-7z"
                          />
                        </svg>
                        25+ Year Warranty
                      </span>
                      <span className="inline-flex items-center px-3 py-1 rounded-full bg-yellow-50 dark:bg-yellow-900/50 text-yellow-700 dark:text-yellow-300 text-sm font-medium">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4 mr-1"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        Subsidy Support
                      </span>
                      <span className="inline-flex items-center px-3 py-1 rounded-full bg-yellow-50 dark:bg-yellow-900/50 text-yellow-700 dark:text-yellow-300 text-sm font-medium">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4 mr-1"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                          />
                        </svg>
                        Performance Guarantee
                      </span>
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>

          {/* Real Estate Section */}
          <div id="realestate" className="mb-20">
            <motion.div
              className="group bg-white dark:bg-gray-800 rounded-3xl shadow-xl overflow-hidden"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
            >
              <div className="flex flex-col lg:flex-row-reverse">
                <motion.div
                  className="lg:w-3/5 relative h-96 lg:h-auto overflow-hidden"
                  variants={fadeInVariants}
                >
                  <img
                    src="https://images.pexels.com/photos/13199169/pexels-photo-13199169.jpeg?auto=compress&cs=tinysrgb&w=600&lazy=load"
                    alt="Real Estate Development"
                    className="w-full h-[800px] object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute top-0 left-0 right-0 bottom-0 bg-gradient-to-l from-black/60 to-transparent"></div>
                  <div className="absolute top-4 right-4 sm:top-6 sm:right-6 md:top-8 md:right-8 bg-white dark:bg-gray-500 rounded-xl p-2 sm:p-3 md:p-4 shadow-md">
                    <div className="h-6 sm:h-7 md:h-8 w-auto flex items-center justify-center">
                      <span className="text-teal-600 font-bold">
                        DEV BHOOMI LUXESTATE
                      </span>
                    </div>
                  </div>

                  <div className="absolute bottom-8 right-8 max-w-sm text-right">
                    <h3 className="text-2xl md:text-3xl font-bold text-white mb-3">
                      Luxury Real Estate
                    </h3>
                    <p className="text-gray-200 mb-6">
                      Premium properties that blend mountain aesthetics with
                      modern comfort.
                    </p>
                    <button
                      onClick={() => {
                        window.open("https://devbhoomiluxestate.com", "_blank");
                      }}
                      className="group relative px-4 py-1 md:px-6 md:py-2 bg-gradient-to-r from-teal-500 to-teal-700 text-white font-normal md:font-medium tracking-wider text-lg rounded-lg overflow-hidden shadow-xl hover:shadow-teal-200 transition-all duration-300 transform hover:-translate-y-1 focus:outline-none"
                    >
                      <span className="relative z-10 flex items-center justify-center">
                        View Properties
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5 ml-2 transform group-hover:translate-x-1 transition-transform"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M14 5l7 7m0 0l-7 7m7-7H3"
                          />
                        </svg>
                      </span>
                    </button>
                  </div>
                </motion.div>

                <motion.div
                  className="lg:w-2/5 p-6 md:p-8 lg:p-12"
                  variants={fadeInVariants}
                >
                  <h4 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">
                    Featured Projects
                  </h4>

                  <div className="space-y-6">
                    <div className="bg-gray-50 dark:bg-gray-700 p-3 md:p-6 rounded-2xl hover:shadow-md transition-all duration-300 border border-gray-100 dark:border-gray-600">
                      <div className="flex items-center mb-3">
                        <div className="w-10 h-10 rounded-full bg-teal-100 dark:bg-teal-900 flex items-center justify-center mr-3">
                          <Home
                            size={20}
                            className="text-teal-600 dark:text-teal-300"
                          />
                        </div>
                        <h5 className="font-bold text-lg text-gray-900 dark:text-white">
                          Mountain View Villas
                        </h5>
                      </div>
                      <p className="text-gray-600 dark:text-gray-300 mb-4">
                        Exclusive hilltop villas with panoramic Himalayan views
                        and sustainable architecture.
                      </p>
                    </div>

                    <div className="bg-gray-50 dark:bg-gray-700 p-3 md:p-6 rounded-2xl hover:shadow-md transition-all duration-300 border border-gray-100 dark:border-gray-600">
                      <div className="flex items-center mb-3">
                        <div className="w-10 h-10 rounded-full bg-teal-100 dark:bg-teal-900 flex items-center justify-center mr-3">
                          <Home
                            size={20}
                            className="text-teal-600 dark:text-teal-300"
                          />
                        </div>
                        <h5 className="font-bold text-lg text-gray-900 dark:text-white">
                          Riverside Apartments
                        </h5>
                      </div>
                      <p className="text-gray-600 dark:text-gray-300 mb-4">
                        Contemporary living spaces alongside the Beas River with
                        smart home technology.
                      </p>
                    </div>

                    <div className="bg-gray-50 dark:bg-gray-700 p-3 md:p-6 rounded-2xl hover:shadow-md transition-all duration-300 border border-gray-100 dark:border-gray-600">
                      <div className="flex items-center mb-3">
                        <div className="w-10 h-10 rounded-full bg-teal-100 dark:bg-teal-900 flex items-center justify-center mr-3">
                          <Home
                            size={20}
                            className="text-teal-600 dark:text-teal-300"
                          />
                        </div>
                        <h5 className="font-bold text-lg text-gray-900 dark:text-white">
                          Cedar Heights Township
                        </h5>
                      </div>
                      <p className="text-gray-600 dark:text-gray-300 mb-4">
                        Integrated township with residential plots, apartments,
                        and commercial spaces in green surroundings.
                      </p>
                    </div>
                  </div>

                  <div className="mt-8">
                    <h4 className="text-lg font-bold mb-4 text-gray-900 dark:text-white">
                      Property Features
                    </h4>
                    <div className="flex flex-wrap gap-3">
                      <span className="inline-flex items-center px-3 py-1 rounded-full bg-teal-50 dark:bg-teal-900/50 text-teal-700 dark:text-teal-300 text-sm font-medium">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4 mr-1"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                          />
                        </svg>
                        Eco-friendly Design
                      </span>
                      <span className="inline-flex items-center px-3 py-1 rounded-full bg-teal-50 dark:bg-teal-900/50 text-teal-700 dark:text-teal-300 text-sm font-medium">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4 mr-1"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13 10V3L4 14h7v7l9-11h-7z"
                          />
                        </svg>
                        Solar Powered
                      </span>
                      <span className="inline-flex items-center px-3 py-1 rounded-full bg-teal-50 dark:bg-teal-900/50 text-teal-700 dark:text-teal-300 text-sm font-medium">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4 mr-1"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                          />
                        </svg>
                        Premium Security
                      </span>
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};
export default DevBhoomiBusinessPage;
